import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Upload, Star } from 'lucide-react';
import api from '@/lib/api';
import { formatINR, resolveImage, getErrorMessage } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CATEGORIES = ['Laundry Care', 'Dishwashing', 'Surface & Glass', 'Bathroom & Utility', 'Hand Wash'];
const EMPTY = { name: '', category: 'Laundry Care', tagline: '', description: '', price: '', compare_at_price: '', size: '', image: '', badge: '', featured: false, stock: 0, rating: 4.5, reviews_count: 0, benefits: [], how_to_use: '', ingredients: '', is_active: true };

function ProductDialog({ open, onClose, product, onSaved }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef();

    useEffect(() => {
        if (product) setForm({ ...EMPTY, ...product, benefits: product.benefits || [], compare_at_price: product.compare_at_price ?? '', badge: product.badge ?? '' });
        else setForm(EMPTY);
    }, [product, open]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        if (!form.name || !form.price) { toast.error('Name and price are required.'); return; }
        setSaving(true);
        const payload = {
            ...form,
            price: Number(form.price),
            compare_at_price: form.compare_at_price === '' ? null : Number(form.compare_at_price),
            stock: Number(form.stock) || 0,
            rating: Number(form.rating) || 4.5,
            reviews_count: Number(form.reviews_count) || 0,
            badge: form.badge || null,
            benefits: Array.isArray(form.benefits) ? form.benefits : String(form.benefits).split('\n').filter(Boolean),
        };
        try {
            let saved;
            if (product) saved = (await api.put(`/admin/products/${product.id}`, payload)).data;
            else saved = (await api.post('/admin/products', payload)).data;
            toast.success(product ? 'Product updated' : 'Product created');
            onSaved(saved);
            onClose();
        } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
        finally { setSaving(false); }
    };

    const uploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!product) { toast.error('Save the product first, then upload an image.'); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post(`/admin/products/${product.id}/image`, fd);
            setForm((f) => ({ ...f, image: data.image }));
            toast.success('Image uploaded');
            onSaved({ ...product, image: data.image });
        } catch (err) { toast.error(getErrorMessage(err, 'Upload failed')); }
        finally { setUploading(false); }
    };

    const benefitsText = Array.isArray(form.benefits) ? form.benefits.join('\n') : form.benefits;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
                <DialogHeader><DialogTitle className="font-display">{product ? 'Edit product' : 'New product'}</DialogTitle><DialogDescription className="sr-only">Fill in the product details and save.</DialogDescription></DialogHeader>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2 flex items-center gap-4">
                        <img src={resolveImage(form.image)} alt="preview" loading="lazy" decoding="async" className="h-24 w-24 rounded-xl object-contain bg-[var(--surface-2)] p-1.5 border border-border" />
                        <div>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="admin-image-file-input" />
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 px-4 rounded-lg border border-border bg-white hover:bg-[var(--surface-2)] font-medium text-sm inline-flex items-center gap-2">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload photo</button>
                            <p className="text-xs text-[var(--ink-muted)] mt-1.5">Or set an image path below. Max 5MB.</p>
                        </div>
                    </div>
                    <div className="sm:col-span-2"><Label>Name</Label><Input data-testid="admin-product-name-input" value={form.name} onChange={set('name')} className="mt-1.5" /></div>
                    <div><Label>Category</Label>
                        <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label>Size</Label><Input value={form.size} onChange={set('size')} placeholder="1 L" className="mt-1.5" /></div>
                    <div><Label>Price (₹)</Label><Input data-testid="admin-product-price-input" type="number" value={form.price} onChange={set('price')} className="mt-1.5" /></div>
                    <div><Label>Compare-at price (₹)</Label><Input type="number" value={form.compare_at_price} onChange={set('compare_at_price')} placeholder="MRP" className="mt-1.5" /></div>
                    <div><Label>Badge</Label><Input value={form.badge} onChange={set('badge')} placeholder="Bestseller / New" className="mt-1.5" /></div>
                    <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={set('stock')} className="mt-1.5" /></div>
                    <div className="sm:col-span-2"><Label>Image path</Label><Input value={form.image} onChange={set('image')} placeholder="/products/xyz.png" className="mt-1.5" /></div>
                    <div className="sm:col-span-2"><Label>Tagline</Label><Input value={form.tagline} onChange={set('tagline')} className="mt-1.5" /></div>
                    <div className="sm:col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={set('description')} rows={3} className="mt-1.5" /></div>
                    <div className="sm:col-span-2"><Label>Benefits (one per line)</Label><Textarea value={benefitsText} onChange={(e) => setForm((f) => ({ ...f, benefits: e.target.value.split('\n') }))} rows={3} className="mt-1.5" /></div>
                    <div className="sm:col-span-2"><Label>How to use</Label><Textarea value={form.how_to_use} onChange={set('how_to_use')} rows={2} className="mt-1.5" /></div>
                    <div className="sm:col-span-2"><Label>Ingredients</Label><Input value={form.ingredients} onChange={set('ingredients')} className="mt-1.5" /></div>
                    <div className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} /><Label>Featured</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} /><Label>Active</Label></div>
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="h-10 px-5 rounded-lg border border-border bg-white hover:bg-[var(--surface-2)] font-medium">Cancel</button>
                    <button data-testid="admin-save-product-button" onClick={save} disabled={saving} className="btn-blue h-10 px-5 rounded-lg font-semibold inline-flex items-center gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState({ open: false, product: null });
    const [toDelete, setToDelete] = useState(null);

    const load = () => { setLoading(true); api.get('/admin/products').then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false)); };
    useEffect(() => { load(); }, []);

    const del = async () => {
        try { await api.delete(`/admin/products/${toDelete.id}`); toast.success('Product deleted'); setProducts((p) => p.filter((x) => x.id !== toDelete.id)); }
        catch { toast.error('Delete failed'); }
        finally { setToDelete(null); }
    };

    return (
        <div>
            <div className="flex items-center justify-between gap-4">
                <div><h1 className="font-display text-2xl lg:text-3xl font-bold">Products</h1><p className="text-[var(--ink-muted)] mt-1">{products.length} products in your store</p></div>
                <button data-testid="admin-create-product-button" onClick={() => setDialog({ open: true, product: null })} className="btn-blue h-11 px-5 rounded-xl font-semibold inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Add product</button>
            </div>

            <div className="mt-6 rounded-2xl border border-border bg-white overflow-hidden">
                {loading ? (
                    <div className="py-24 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-[var(--brand-blue)]" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table data-testid="admin-products-table" className="w-full text-sm">
                            <thead><tr className="bg-[var(--surface-2)] text-left text-[var(--ink-muted)]">
                                <th className="px-4 py-3 font-medium">Product</th>
                                <th className="px-4 py-3 font-medium hidden md:table-cell">Category</th>
                                <th className="px-4 py-3 font-medium">Price</th>
                                <th className="px-4 py-3 font-medium hidden md:table-cell">Stock</th>
                                <th className="px-4 py-3 font-medium hidden sm:table-cell">Status</th>
                                <th className="px-4 py-3 font-medium text-right">Actions</th>
                            </tr></thead>
                            <tbody>
                                {products.map((p) => (
                                    <tr key={p.id} className="border-t border-border">
                                        <td className="px-4 py-3"><div className="flex items-center gap-3"><img src={resolveImage(p.image)} alt={p.name} loading="lazy" decoding="async" className="h-11 w-11 rounded-lg object-contain bg-[var(--surface-2)] p-1" /><div><p className="font-medium flex items-center gap-1.5">{p.name} {p.featured && <Star className="h-3.5 w-3.5 fill-[var(--brand-yellow)] text-[var(--brand-yellow)]" />}</p><p className="text-xs text-[var(--ink-muted)]">{p.size}</p></div></div></td>
                                        <td className="px-4 py-3 hidden md:table-cell text-[var(--ink-muted)]">{p.category}</td>
                                        <td className="px-4 py-3 font-semibold">{formatINR(p.price)}</td>
                                        <td className="px-4 py-3 hidden md:table-cell">{p.stock}</td>
                                        <td className="px-4 py-3 hidden sm:table-cell"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{p.is_active ? 'Active' : 'Hidden'}</span></td>
                                        <td className="px-4 py-3"><div className="flex items-center justify-end gap-2">
                                            <button data-testid={`admin-edit-${p.slug}`} onClick={() => setDialog({ open: true, product: p })} className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-[var(--surface-2)]"><Pencil className="h-4 w-4" /></button>
                                            <button data-testid={`admin-delete-${p.slug}`} onClick={() => setToDelete(p)} className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-red-50 hover:border-red-200 text-red-500"><Trash2 className="h-4 w-4" /></button>
                                        </div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ProductDialog open={dialog.open} product={dialog.product} onClose={() => setDialog({ open: false, product: null })} onSaved={() => load()} />

            <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete product?</AlertDialogTitle><AlertDialogDescription>This will permanently remove "{toDelete?.name}" from your store.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
